import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { CATEGORIES, slugify } from "../lib/utils-app";
import { toast } from "sonner";
import { Mail, Scale } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      toast.success("Subscribed. Your Friday intelligence digest is on its way.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <footer className="mt-20 border-t border-[var(--io-border)] bg-[var(--io-bg)]" data-testid="site-footer">
      <div className="max-w-[1380px] mx-auto px-4 md:px-8 py-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-9 h-9 rounded-md border border-[var(--io-gold)]/40 bg-gradient-to-br from-[var(--io-gold)]/15 to-transparent">
              <span className="font-serif text-lg text-[var(--io-gold)]">iO</span>
            </span>
            <div className="font-serif text-2xl text-[var(--io-text)]">India<span className="text-[var(--io-gold)]">ODR</span></div>
          </div>
          <p className="kicker mt-2" style={{ color: "#D4AF37" }}>Arbitration & ODR Intelligence</p>
          <p className="mt-5 text-[13px] leading-relaxed text-[var(--io-text-2)] max-w-sm">
            AI-assisted, human-reviewed intelligence on global arbitration, Indian disputes,
            online dispute resolution, award enforcement and institutional updates. Every article
            is source-backed. We do not publish legal advice.
          </p>
          <div className="mt-5 flex gap-2 flex-wrap">
            <span className="io-badge io-badge-blue">AI-assisted</span>
            <span className="io-badge io-badge-green">Human reviewed</span>
            <span className="io-badge io-badge-gold">Source-backed</span>
          </div>
        </div>

        <div className="md:col-span-5 grid grid-cols-2 gap-x-8 gap-y-2">
          <div>
            <div className="kicker mb-3">Topics</div>
            <ul className="space-y-2 text-[13px]">
              {CATEGORIES.slice(0, 7).map((c) => (
                <li key={c}>
                  <Link to={`/category/${slugify(c)}`} className="text-[var(--io-text-2)] hover:text-[var(--io-gold)]" data-testid={`footer-cat-${slugify(c)}`}>{c}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="kicker mb-3">Institution</div>
            <ul className="space-y-2 text-[13px] text-[var(--io-text-2)]">
              <li><Link to="/about" className="hover:text-[var(--io-gold)]" data-testid="footer-about">About</Link></li>
              <li><Link to="/editorial-policy" className="hover:text-[var(--io-gold)]" data-testid="footer-editorial-policy">Editorial Policy</Link></li>
              <li><Link to="/correction-policy" className="hover:text-[var(--io-gold)]" data-testid="footer-correction-policy">Correction Policy</Link></li>
              <li><Link to="/contact" className="hover:text-[var(--io-gold)]" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/digest" className="hover:text-[var(--io-gold)]" data-testid="footer-digest">Weekly Digest</Link></li>
              <li><a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="hover:text-[var(--io-gold)]" data-testid="footer-sitemap">Sitemap</a></li>
            </ul>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="kicker mb-2" style={{ color: "#D4AF37" }}>Weekly Intelligence Digest</div>
          <p className="text-[13px] text-[var(--io-text-2)] mb-4">Friday's essential arbitration developments, curated.</p>
          <form onSubmit={subscribe} className="flex flex-col gap-2" data-testid="newsletter-form">
            <div className="flex items-center gap-2 border border-[var(--io-border)] focus-within:border-[var(--io-gold)] rounded-md px-3 bg-[var(--io-bg-2)]">
              <Mail size={14} className="text-[var(--io-muted)]" />
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@firm.com"
                data-testid="newsletter-email-input"
                className="flex-1 bg-transparent py-2.5 text-[13px] outline-none text-[var(--io-text)] placeholder:text-[var(--io-muted)]"
              />
            </div>
            <button type="submit" disabled={submitting} className="btn btn-primary justify-center" data-testid="newsletter-submit-button">
              {submitting ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-[var(--io-border)]">
        <div className="max-w-[1380px] mx-auto px-4 md:px-8 py-5 flex flex-wrap items-center justify-between gap-3 text-[11px] font-mono text-[var(--io-muted)]">
          <div className="flex items-center gap-2">
            <Scale size={12} className="text-[var(--io-gold)]" />
            <span>© {new Date().getFullYear()} IndiaODR · News analysis, not legal advice</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/editorial-policy" className="hover:text-[var(--io-gold)]">Editorial</Link>
            <Link to="/correction-policy" className="hover:text-[var(--io-gold)]">Corrections</Link>
            <span>v1.0</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
